import { useMemo, useState } from 'react';
import { Box, FormControlLabel, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import { DataGridPremium, GridEditInputCell } from '@mui/x-data-grid-premium';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ThemeButton from 'shared/ui/Button';
import { ITEM_COLUMNS } from '../../../lib/packingListFields';
import { newDraftLine } from '../../../lib/packingListDraft';
import { ensureQuantities, secondaryQuantities, updatePrimaryFromCells } from '../../../lib/packingListQuantity';
import { LOW_CONFIDENCE } from '../../../lib/packingListValidation';
import { describeSourceRef } from '../../../lib/packingListWire';
import { gridSx, panelTitleSx } from 'shared/config/Styles';
import { REVIEW_TABLE_HEAD_BG, reviewPanelHeadSx, reviewPanelSx } from '../../../lib/packingListReviewStyles';
import UomSelect, { UomCell } from './UomSelect';
import LotEditCell from './LotEditCell';
import OptionSelectCell from './OptionSelectCell';
import ConfidenceMark from './ConfidenceMark';
import QuantityEditor, { QuantitiesCell } from './QuantityEditor';
import AllocatedMarker from './AllocatedMarker';

/**
 * 내용 칸의 너비. 정해 둔 너비를 최소로 두고, 그리드가 그보다 넓으면(원본 미리보기가 없는 수기 문서 등) 남는 폭을 그 너비 비율대로 나눠 채운다.
 * 라인 번호(#) 칸은 넓어질 이유가 없어 고정 너비로 둔다.
 */
const fillWidth = (width) => ({ width, minWidth: width, flex: width });

/** 행 삭제 칸은 오른쪽에 고정한다. 보기 전용에는 그 칸이 없다. 읽기 전용에서 Edit으로 바뀌어도 맞도록 매번 넘긴다. */
const PINNED_ACTIONS = { right: ['__actions'] };
const PINNED_NONE = { right: [] };

/** Other Qty가 두 줄인 행의 높이. 나머지 행은 기본 높이(31)다. */
const TWO_LINE_ROW_HEIGHT = 40;
/** 11px 글자 한 자의 넉넉한 폭과 셀 좌우 여백. 한 줄에 다 들어가는지 어림할 때 쓴다. */
const CHAR_WIDTH = 7;
const CELL_PADDING = 16;

/** 보이는 글자 칸 중 하나라도 한 줄에 다 안 들어가면 그 행만 내용 높이로 늘린다. */
const needsWrap = (row, columns) => columns.some((c) => String(row[c.field] ?? '').length * CHAR_WIDTH > c.width - CELL_PADDING);

/** 셀 값과 신뢰도 점을 함께 그린다. 비어 있는 필수 셀은 빨간 안내문, 긴 값은 줄바꿈, # 칸은 원문 위치 툴팁. */
function CellContent({ params, col }) {
    const value = params.value;
    const empty = value === null || value === undefined || value === '';
    const body = (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', lineHeight: 1.4, display: 'flex', alignItems: 'center', px: 1, whiteSpace: 'normal', wordBreak: 'break-word', justifyContent: (empty && !col.required) || col.align === 'center' ? 'center' : col.type === 'number' ? 'flex-end' : 'flex-start' }}>
            {empty ? <Typography sx={{ fontSize: 11, color: col.required ? 'error.main' : 'text.disabled', fontWeight: col.required ? 600 : 400 }}>{col.required ? 'Required' : '—'}</Typography> : String(value)}
            <ConfidenceMark confidence={params.row.field_confidence?.[col.field]} field={col.field} edited={(params.row.edited_fields ?? []).includes(col.field)} note={params.row.field_notes?.[col.field]} />
        </Box>
    );
    if (col.field !== 'line_no') return body;
    const where = describeSourceRef(params.row.source_ref);
    return <Tooltip title={where ? `Read from the original · ${where}` : 'Added manually · no location in the original'} placement="right" arrow>{body}</Tooltip>;
}

/** 아직 확인하지 않은 낮은 신뢰도 셀의 수. 범례로 보여 점이 무엇인지 알린다. */
const countLowConfidence = (lines) =>
    lines.reduce((n, line) => n + Object.entries(line.field_confidence ?? {}).filter(([field, conf]) => typeof conf === 'number' && conf < LOW_CONFIDENCE && !(line.edited_fields ?? []).includes(field)).length, 0);

/**
 * il_pl_item 라인을 셀 단위로 고치는 그리드. 기본은 핵심 컬럼만, "All columns" 스위치로 카톤·중량·부피·LOT를 연다.
 * 행 추가·삭제·드래그 순서변경, 신뢰도 낮은 셀 표시, 검증 항목에서 넘어온 셀 강조, 원본 뷰어와의 양방향 하이라이트를 담당한다.
 * Qty·UOM 칸은 대표 수량이고, 같은 품목의 다른 단위 수량은 Other Qty 칸을 누르거나 Enter로 여는 수량 편집 창에서 더하고 대표를 바꾼다.
 * lineMarkers(라인 id → 배분·영향)가 있으면(확정본 수정) 맨 앞에 배분된 라인 표시 칸을 둔다.
 * readOnly(확정본 보기)면 셀을 고칠 수 없고, 행 추가·삭제·순서 변경과 수량 편집 창도 없다.
 */
export default function EditableGrid({ apiRef, lines, onLinesChange, activeRef, onActiveRef, highlightCell, issuesByCell, uomAliases, onUomAlias, lineMarkers = null, readOnly = false }) {
    const [showAll, setShowAll] = useState(false);
    const lowConfidenceCount = useMemo(() => countLowConfidence(lines), [lines]);
    /** 수량 편집 창. Other Qty 칸을 누르거나 그 칸에서 Enter를 치면 연다. { lineId, anchorEl } */
    const [quantityEditor, setQuantityEditor] = useState(null);
    const editingLine = quantityEditor ? lines.find((l) => l.id === quantityEditor.lineId) : null;

    const columns = useMemo(() => {
        const defs = ITEM_COLUMNS.filter((c) => showAll || c.compact).map((col) => {
            const def = { field: col.field, headerName: col.headerName, ...(col.field === 'line_no' ? { width: col.width } : fillWidth(col.width)), type: col.type === 'number' ? 'number' : 'string', editable: !readOnly && !col.readOnly, headerAlign: 'center', align: col.align ?? (col.type === 'number' ? 'right' : 'left'), renderCell: (p) => <CellContent params={p} col={col} /> };
            if (col.editor === 'uom') {
                def.display = 'flex';
                def.renderCell = (p) => (
                    <Box sx={{ position: 'relative', width: '100%' }}>
                        <UomCell value={p.value} uomAliases={uomAliases} />
                        <ConfidenceMark confidence={p.row.field_confidence?.qty_uom} field="qty_uom" edited={(p.row.edited_fields ?? []).includes('qty_uom')} note={p.row.field_notes?.qty_uom} />
                    </Box>
                );
                def.renderEditCell = (p) => <UomSelect {...p} uomAliases={uomAliases} onAlias={onUomAlias} maxLength={col.maxLength} />;
            }
            if (col.editor === 'lot') def.renderEditCell = (p) => <LotEditCell {...p} maxLength={col.maxLength} />;
            if (col.options) def.renderEditCell = (p) => <OptionSelectCell {...p} options={col.options} />;
            else if (!col.editor && col.maxLength) def.renderEditCell = (p) => <GridEditInputCell {...p} inputProps={{ maxLength: col.maxLength }} />;
            if (col.editor === 'date') def.renderEditCell = (p) => <LotEditCell {...p} type="date" />;
            if (col.editor !== 'uom') return [def];
            const quantitiesCol = { field: 'quantities', headerName: 'Other Qty', ...fillWidth(150), display: 'flex', editable: false, sortable: false, headerAlign: 'center', renderCell: (p) => <QuantitiesCell line={p.row} /> };
            return [def, quantitiesCol];
        }).flat();
        if (lineMarkers) defs.unshift({ field: '__allocated', headerName: '', width: 84, sortable: false, editable: false, align: 'center', renderCell: (p) => <AllocatedMarker marker={lineMarkers[p.id]} /> });
        defs.push({ field: 'cntr_hint', headerName: 'PL Container', ...fillWidth(130), editable: !readOnly, headerAlign: 'center', renderCell: (p) => (p.value ? <Tooltip title="Container number written on the PL for this line. It must be on the B/L to confirm and guides auto allocation. Fix or clear it if it was read wrong." arrow><Typography sx={{ fontSize: 11, px: 1, color: 'text.secondary' }}>On PL: {p.value}</Typography></Tooltip> : <Typography sx={{ fontSize: 11, px: 1, width: '100%', textAlign: 'center', color: 'text.disabled' }}>—</Typography>) });
        if (!readOnly) defs.push({ field: '__actions', headerName: '', width: 30, minWidth: 30, sortable: false, align: 'center', display: 'flex', renderCell: (p) => (<IconButton size="small" title="Delete line" sx={{ p: '2px' }} onClick={() => onLinesChange((prev) => prev.filter((l) => l.id !== p.id).map((l, i) => ({ ...l, line_no: i + 1 })))}><DeleteOutlineIcon sx={{ fontSize: 15 }} /></IconButton>) });
        return defs;
    }, [onLinesChange, uomAliases, onUomAlias, showAll, lineMarkers, readOnly]);

    const textColumns = useMemo(() => ITEM_COLUMNS.filter((c) => (showAll || c.compact) && c.type !== 'number' && c.editor !== 'uom'), [showAll]);

    /** 셀을 고치면 어떤 필드를 손댔는지 기록해 신뢰도 경고를 거둔다. 항상 최신 상태 위에 반영해 동시 편집이 유실되지 않게 한다. */
    const processRowUpdate = (newRow, oldRow) => {
        const changed = Object.keys(newRow).filter((k) => newRow[k] !== oldRow[k]);
        const edited = { ...newRow, edited_fields: [...new Set([...(newRow.edited_fields ?? []), ...changed])] };
        const next = changed.includes('qty') || changed.includes('qty_uom') ? updatePrimaryFromCells(edited) : edited;
        onLinesChange((prev) => prev.map((l) => (l.id === next.id ? next : l)));
        return next;
    };

    const handleRowOrderChange = ({ oldIndex, targetIndex }) => {
        onLinesChange((prev) => {
            const next = [...prev];
            const [moved] = next.splice(oldIndex, 1);
            next.splice(targetIndex, 0, moved);
            return next.map((l, i) => ({ ...l, line_no: i + 1 }));
        });
    };

    return (
        <Box sx={{ ...reviewPanelSx, height: '100%', minHeight: 0 }}>
            <Box sx={reviewPanelHeadSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                    <Typography sx={panelTitleSx}>Line Items ({lines.length})</Typography>
                    {lowConfidenceCount > 0 && (
                        <Tooltip title="Cells the parser read with less than 80% confidence. Compare them with the original; editing a cell clears its mark." arrow>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: 'text.secondary' }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main' }} />
                                {lowConfidenceCount} low-confidence cell{lowConfidenceCount === 1 ? '' : 's'}
                            </Box>
                        </Tooltip>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControlLabel control={<Switch size="small" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label="All columns" sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: 12 } }} />
                    {!readOnly && <ThemeButton text="NEW LINE" icon={<AddIcon />} sx={{ width: 'auto', px: 1.25, gap: 0.5, fontSize: 12, fontWeight: 500, color: '#003a52', bgcolor: '#e6eef2', boxShadow: 'none', '&:hover': { bgcolor: '#d5e3ea', boxShadow: 'none' } }} onClick={() => onLinesChange((prev) => [...prev, ensureQuantities(newDraftLine({ line_no: prev.length + 1, po_number: prev[0]?.po_number ?? '', qty_uom: prev[0]?.qty_uom ?? 'PCS', weight_uom: prev[0]?.weight_uom ?? 'KG', volume_uom: prev[0]?.volume_uom ?? 'CBM' }))])} />}
                </Box>
            </Box>
            <DataGridPremium
                apiRef={apiRef}
                rows={lines}
                columns={columns}
                density="compact"
                editMode="cell"
                rowReordering={!readOnly}
                onRowOrderChange={handleRowOrderChange}
                pinnedColumns={readOnly ? PINNED_NONE : PINNED_ACTIONS}
                hideFooter
                disableColumnMenu
                disableColumnSorting
                disableRowSelectionOnClick
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={(e) => console.error(e)}
                onCellClick={(p, event) => {
                    if (p.field === 'quantities' && !readOnly) setQuantityEditor({ lineId: p.id, anchorEl: event.currentTarget });
                    const line = lines.find((l) => l.id === p.id);
                    onActiveRef(line?.source_ref ? { ...line.source_ref, lineId: line.id } : { lineId: p.id });
                }}
                onCellKeyDown={(p, event) => {
                    if (readOnly || p.field !== 'quantities' || event.key !== 'Enter') return;
                    event.preventDefault();
                    setQuantityEditor({ lineId: p.id, anchorEl: event.currentTarget });
                }}
                getRowClassName={(p) => (activeRef?.lineId === p.id ? 'pl-row-active' : '')}
                getCellClassName={(p) => [issuesByCell[`${p.id}:${p.field}`] ?? '', highlightCell && highlightCell.rowId === p.id && highlightCell.field === p.field ? 'pl-cell-target' : ''].join(' ')}
                rowHeight={31}
                getRowHeight={({ model }) => (needsWrap(model, textColumns) ? 'auto' : secondaryQuantities(model.quantities ?? []).length >= 2 ? TWO_LINE_ROW_HEIGHT : null)}
                columnHeaderHeight={32}
                sx={{
                    ...gridSx,
                    '& .MuiDataGrid-columnHeader': { backgroundColor: REVIEW_TABLE_HEAD_BG },
                    '& .MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: '4px' },
                    // 편집 중인 셀은 합계 대조 표의 입력 칸과 같은 얇은 테두리만 두고, 입력 칸이 행보다 커져 행 높이가 출렁이지 않게 한다.
                    '& .MuiDataGrid-cell--editing': { boxShadow: 'none', '&:focus-within': { outline: 'none' } },
                    '& .MuiDataGrid-cell--editing .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiDataGrid-cell--editing .MuiInputBase-root': { height: 20, py: '0 !important', borderRadius: '4px', bgcolor: 'white', outline: '1px solid #00647e' },
                    '& .pl-row-active': { bgcolor: 'rgba(0,100,126,0.08)' },
                    '& .pl-cell-error': { bgcolor: 'rgba(217,41,54,0.12)' },
                    '& .pl-cell-warning': { bgcolor: 'rgba(244,135,4,0.12)' },
                    '& .pl-cell-target': { outline: '2px solid #d92936', outlineOffset: -2 },
                }}
            />
            {editingLine && <QuantityEditor anchorEl={quantityEditor.anchorEl} line={editingLine} onClose={() => setQuantityEditor(null)} onChange={(update) => onLinesChange((prev) => prev.map((l) => (l.id === editingLine.id ? update(l) : l)))} />}
        </Box>
    );
}
